import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Container,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import GrowthSimulation from '../../components/common/GrowthSimulation';
import Layout from '../../components/layout/Layout';
import { SurgicalProcedureCoverage } from '../../types';
import SurgicalProcedureCard from './components/SurgicalProcedureCard';
import SurgicalProcedureRow from './components/SurgicalProcedureRow';
import { useInfiniteSurgicalProcedures } from './hooks/useInfiniteSurgicalProcedures';

const SurgicalProcedures = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { procedures, loading, error, hasMore, fetchMore, total } = useInfiniteSurgicalProcedures();
  const [percentageValue, setPercentageValue] = useState<number | string>(0);

  const scrollTrigger = useScrollTrigger({
    threshold: 200,
    disableHysteresis: true,
  });

  const tableColumns = ['Código CPT', 'Procedimiento', 'Total', '   '];

  return (
    <Layout showSidebar={true}>
      {!isMobile ? (
        <Container
          maxWidth='lg'
          sx={{
            py: 4,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              mb: 4,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
              borderRadius: 3,
              p: 4,
              border: `1px solid ${theme.palette.primary.main}20`,
            }}
          >
            <Typography
              variant='h3'
              fontWeight={700}
              color='primary'
              sx={{
                mb: 1,
                letterSpacing: '-0.02em',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Procedimientos Quirúrgicos
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ fontSize: '1.1rem' }}>
              {total} procedimientos disponibles
            </Typography>
          </Box>

          {/* Growth Simulation */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <GrowthSimulation
              percentageValue={percentageValue}
              setPercentageValue={setPercentageValue}
            />
          </Box>

          {/* Table Container */}
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Table Header */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                px: 3,
                py: 2,
              }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((header, index) => {
                      let alignment: 'left' | 'center' | 'right' = 'left';
                      if (index === 0) alignment = 'left';
                      // Código CPT
                      else if (index === 1) alignment = 'left';
                      // Procedimiento
                      else if (index === 2) alignment = 'right';
                      // Total
                      else if (index === 3) alignment = 'center';
                      // Botón expandir

                      return (
                        <TableCell
                          key={index}
                          align={alignment}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            textAlign: alignment,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            width:
                              index === 0
                                ? '150px' // Código CPT
                                : index === 1
                                  ? '400px' // Procedimiento
                                  : index === 2
                                    ? '150px' // Total
                                    : '60px', // Botón
                            minWidth:
                              index === 0
                                ? '150px'
                                : index === 1
                                  ? '400px'
                                  : index === 2
                                    ? '150px'
                                    : '60px',
                            paddingLeft: index === 0 || index === 1 ? 2 : undefined,
                            paddingRight: index === 3 ? 1 : index === 2 ? 2 : 2,
                            py: 2,
                            borderBottom: 'none',
                          }}
                        >
                          {header}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
              </Table>
            </Box>

            {/* Scrollable Content */}
            <Box
              id='scrollable-table-surgical'
              sx={{
                maxHeight: 'calc(100vh - 300px)',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.primary.light,
                  borderRadius: '4px',
                  '&:hover': {
                    background: theme.palette.primary.main,
                  },
                },
              }}
            >
              <InfiniteScroll
                dataLength={procedures.length}
                next={fetchMore}
                hasMore={hasMore}
                loader={
                  <Box
                    p={4}
                    width='100%'
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        border: `3px solid ${theme.palette.primary.light}`,
                        borderTop: `3px solid ${theme.palette.primary.main}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                    <Typography textAlign='center' color='text.secondary' variant='body2'>
                      Cargando más procedimientos...
                    </Typography>
                  </Box>
                }
                scrollableTarget='scrollable-table-surgical'
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableBody>
                    {loading && procedures.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align='center' sx={{ py: 10 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                border: `4px solid ${theme.palette.primary.light}`,
                                borderTop: `4px solid ${theme.palette.primary.main}`,
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                '@keyframes spin': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' },
                                },
                              }}
                            />
                            <Typography color='text.secondary' fontWeight={500}>
                              Cargando Procedimientos...
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={4} align='center' sx={{ py: 10 }}>
                          <Box
                            sx={{
                              p: 3,
                              borderRadius: 2,
                              backgroundColor: `${theme.palette.error.main}10`,
                              border: `1px solid ${theme.palette.error.main}30`,
                            }}
                          >
                            <Typography color='error' fontWeight={600}>
                              {error}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : procedures.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align='center' sx={{ py: 10 }}>
                          <Box
                            sx={{
                              p: 3,
                              borderRadius: 2,
                              backgroundColor: `${theme.palette.grey[100]}`,
                            }}
                          >
                            <Typography color='text.secondary' fontWeight={500}>
                              No se encontraron procedimientos
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      procedures.map((procedure: SurgicalProcedureCoverage) => (
                        <SurgicalProcedureRow
                          key={procedure._id}
                          procedure={procedure}
                          percentageValue={percentageValue}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </Box>
          </Box>
        </Container>
      ) : (
        // Vista para dispositivos móviles (tarjetas)
        <Box sx={{ px: 2, py: 3 }}>
          {/* Header Section */}
          <Box
            sx={{
              mb: 3,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
              borderRadius: 3,
              p: 3,
              border: `1px solid ${theme.palette.primary.main}20`,
            }}
          >
            <Typography
              variant='h4'
              fontWeight={700}
              color='primary'
              sx={{
                mb: 1,
                letterSpacing: '-0.02em',
              }}
            >
              Procedimientos Quirúrgicos
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ fontSize: '1rem' }}>
              {total} procedimientos disponibles
            </Typography>
          </Box>

          {/* Growth Simulation */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <GrowthSimulation
              percentageValue={percentageValue}
              setPercentageValue={setPercentageValue}
            />
          </Box>

          {/* Cards */}
          <Box sx={{ minHeight: '60vh' }}>
            {loading && procedures.length === 0 ? (
              <Box
                py={10}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    border: `4px solid ${theme.palette.primary.light}`,
                    borderTop: `4px solid ${theme.palette.primary.main}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                <Typography color='text.secondary' fontWeight={500}>
                  Cargando Procedimientos...
                </Typography>
              </Box>
            ) : error ? (
              <Box
                py={10}
                sx={{
                  px: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.error.main}10`,
                    border: `1px solid ${theme.palette.error.main}30`,
                    maxWidth: '400px',
                    width: '100%',
                  }}
                >
                  <Typography color='error' fontWeight={600} textAlign='center'>
                    {error}
                  </Typography>
                </Box>
              </Box>
            ) : procedures.length === 0 ? (
              <Box
                py={10}
                sx={{
                  px: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: `${theme.palette.grey[100]}`,
                    maxWidth: '400px',
                    width: '100%',
                  }}
                >
                  <Typography color='text.secondary' fontWeight={500} textAlign='center'>
                    No se encontraron procedimientos
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                {procedures.map((procedure: SurgicalProcedureCoverage) => (
                  <SurgicalProcedureCard
                    key={procedure._id}
                    procedure={procedure}
                    percentageValue={percentageValue}
                  />
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Button
                      variant='contained'
                      onClick={fetchMore}
                      disabled={loading}
                      sx={{
                        minWidth: 200,
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {loading ? 'Cargando...' : 'Cargar Más'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Floating button to scroll to top on mobile */}
          {scrollTrigger && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <Fab
                onClick={() =>
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  })
                }
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  },
                }}
              >
                <KeyboardArrowUpIcon />
              </Fab>
            </Box>
          )}
        </Box>
      )}
    </Layout>
  );
};

export default SurgicalProcedures;
